import { useState } from 'react'
import { RotateCcw } from 'lucide-react'

import { pluralize } from '@/lib/pluralize'
import { Button } from '@/ui/button'
import { Slider } from '@/ui/slider'

import { useFilterSource } from './data-grid-filter-source'
import {
  DataGridFilterBody,
  DataGridFilterHeader,
  DataGridFilterPanel,
} from './data-grid-filter-panel'
import { DataGridFilterChip, type DataGridFilterPresentationProps } from './data-grid-filter-shared'

const MIN_HOURS = 1
const MAX_HOURS = 24 * 365
const PRESETS = [
  { hours: 1, label: 'Past hour' },
  { hours: 24, label: 'Past 24 hours' },
  { hours: 24 * 7, label: 'Past 7 days' },
  { hours: 24 * 30, label: 'Past 30 days' },
] as const
const SLIDER_STEPS = 1000
const DEFAULT_HOURS = 24 * 30
const HOURS_PER_DAY = 24
const HOURS_PER_WEEK = HOURS_PER_DAY * 7
const HOURS_PER_AVERAGE_MONTH = HOURS_PER_DAY * (365 / 12)
const cutoffFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function hoursToSliderValue(hours: number): number {
  const progress = Math.log(hours / MIN_HOURS) / Math.log(MAX_HOURS / MIN_HOURS)
  return Math.round(progress * SLIDER_STEPS)
}

function sliderValueToHours(value: number): number {
  const preset = PRESETS.find((item) => hoursToSliderValue(item.hours) === value)
  if (preset) return preset.hours
  const progress = value / SLIDER_STEPS
  return Math.max(
    MIN_HOURS,
    Math.min(
      MAX_HOURS,
      Math.round(MIN_HOURS * (MAX_HOURS / MIN_HOURS) ** progress),
    ),
  )
}

export function formatRecentWindow(hours: number): string {
  const preset = PRESETS.find((item) => item.hours === hours)
  if (preset) return preset.label
  if (hours === MAX_HOURS) return 'Past year'

  if (hours < HOURS_PER_DAY * 2) {
    return `Past ${pluralize(hours, 'hour')}`
  }
  if (hours < HOURS_PER_DAY * 14) {
    const days = Math.round(hours / HOURS_PER_DAY)
    return `Past ${pluralize(days, 'day')}`
  }
  if (hours < HOURS_PER_DAY * 60) {
    const weeks = Math.round(hours / HOURS_PER_WEEK)
    return `Past ${pluralize(weeks, 'week')}`
  }

  const months = Math.round(hours / HOURS_PER_AVERAGE_MONTH)
  return months >= 12 ? 'Past year' : `Past ${months} months`
}

/** A length of time without the "Past" prefix, such as "4 hours" or "2 weeks". */
export function formatPeriod(hours: number): string {
  if (hours < HOURS_PER_DAY * 2) return pluralize(hours, 'hour')
  if (hours < HOURS_PER_DAY * 14) return pluralize(Math.round(hours / HOURS_PER_DAY), 'day')
  if (hours < HOURS_PER_DAY * 60) return pluralize(Math.round(hours / HOURS_PER_WEEK), 'week')
  const months = Math.round(hours / HOURS_PER_AVERAGE_MONTH)
  return months >= 12 ? pluralize(Math.round(hours / (HOURS_PER_DAY * 365)), 'year') : `${months} months`
}

export function formatRecentCutoff(hours: number, now: Date = new Date()): string {
  const cutoff = new Date(now.getTime() - hours * 60 * 60 * 1000)
  return `Since ${cutoffFormatter.format(cutoff)}`
}

type Period = { amount: number; unit: 'hours' | 'days' }
type Mode = 'within' | 'older' | 'between'
type Age = { min?: Period; max?: Period }

const MODES: { mode: Mode; label: string; operator: string }[] = [
  { mode: 'within', label: 'Within', operator: 'relative' },
  { mode: 'older', label: 'Older than', operator: 'age' },
  { mode: 'between', label: 'Between', operator: 'age' },
]
const OLDER_PRESETS = [
  { hours: 1, label: 'Over an hour ago' },
  { hours: 24, label: 'Over a day ago' },
  { hours: 24 * 7, label: 'Over 7 days ago' },
  { hours: 24 * 30, label: 'Over 30 days ago' },
] as const
const dateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })

function toHours(period: Period) {
  return period.amount * (period.unit === 'days' ? HOURS_PER_DAY : 1)
}

function toPeriod(hours: number): Period {
  return hours % HOURS_PER_DAY === 0 ? { amount: hours / HOURS_PER_DAY, unit: 'days' } : { amount: hours, unit: 'hours' }
}

function formatDate(value: unknown) {
  return typeof value === 'string' ? dateFormatter.format(new Date(value)) : String(value)
}

/** The chip summary for any timestamp filter, including fixed dates this editor cannot change. */
export function timestampSummary(operator: string, values: unknown): string {
  if (operator === 'relative') return formatRecentWindow(toHours(values as Period))
  if (operator === 'age') {
    const { min, max } = values as Age
    if (min && max) return `${formatPeriod(toHours(min))} – ${formatPeriod(toHours(max))} ago`
    if (min) return `Over ${formatPeriod(toHours(min))} ago`
    return formatRecentWindow(toHours(max!))
  }
  if (operator === 'before') return `Before ${formatDate(values)}`
  if (operator === 'after') return `After ${formatDate(values)}`
  if (operator === 'between') {
    const range = values as { from: string; to: string }
    return `${formatDate(range.from)} – ${formatDate(range.to)}`
  }
  if (operator === 'empty') return values ? 'Empty' : 'Has a value'
  return 'Filtered'
}

export function DataGridTimestampFilter({
  column,
  presentation,
  onBack,
}: DataGridFilterPresentationProps) {
  const grid = useFilterSource()
  const current = grid.filters?.find((filter) => filter.column === column.id)
  const modes = MODES.filter((item) => column.operators.includes(item.operator))
  const age = current?.operator === 'age' ? current.values as Age : null
  const relativeHours = current?.operator === 'relative' ? toHours(current.values as Period)
    : age && !age.min && age.max ? toHours(age.max) : null
  const currentMode: Mode | null = relativeHours !== null ? 'within'
    : age?.min && age.max ? 'between' : age?.min ? 'older' : null
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>(currentMode ?? modes[0]?.mode ?? 'within')
  const [draftHours, setDraftHours] = useState(relativeHours ?? DEFAULT_HOURS)
  const [draftMin, setDraftMin] = useState(age?.min ? toHours(age.min) : 24)
  const [draftMax, setDraftMax] = useState(age?.max ? toHours(age.max) : HOURS_PER_WEEK)

  function changeOpen(next: boolean) {
    if (next) {
      setMode(currentMode ?? modes[0]?.mode ?? 'within')
      setDraftHours(relativeHours ?? DEFAULT_HOURS)
      setDraftMin(age?.min ? toHours(age.min) : 24)
      setDraftMax(age?.max ? toHours(age.max) : HOURS_PER_WEEK)
    }
    setOpen(next)
  }

  function apply(next: { operator: string; values: Period | Age } | null) {
    grid.setFilters([
      ...(grid.filters ?? []).filter((filter) => filter.column !== column.id),
      ...(next === null ? [] : [{ column: column.id, ...next }]),
    ] as never)
  }

  const within = (hours: number) => apply(column.operators.includes('relative')
    ? { operator: 'relative', values: toPeriod(hours) }
    : { operator: 'age', values: { max: toPeriod(hours) } })
  const older = (hours: number) => apply({ operator: 'age', values: { min: toPeriod(hours) } })
  const between = (min: number, max: number) =>
    apply({ operator: 'age', values: { min: toPeriod(Math.min(min, max)), max: toPeriod(Math.max(min, max)) } })

  const summary = current ? timestampSummary(current.operator, current.values) : undefined
  const slider = (label: string, value: number, onChange: (hours: number) => void, onCommit: (hours: number) => void, format = formatRecentWindow) => (
    <div className="flex flex-col gap-1">
      <Slider
        label={label}
        min={0}
        max={SLIDER_STEPS}
        value={hoursToSliderValue(value)}
        onValueChange={(next) => onChange(sliderValueToHours(next))}
        onValueCommitted={(next) => onCommit(sliderValueToHours(next))}
        formatValue={(next) => format(sliderValueToHours(next))}
      />
      <div className="text-muted-foreground flex justify-between text-[11px]">
        <span>1 hour</span>
        <span>1 year</span>
      </div>
    </div>
  )

  const panel = (
    <DataGridFilterPanel presentation={presentation} className="w-80">
      <DataGridFilterHeader
        label={column.label}
        description="Show records by how long ago they were observed."
        onBack={onBack}
      />
      <DataGridFilterBody className="gap-3">
        {modes.length > 1 && (
          <div role="radiogroup" aria-label="Time range" className="bg-muted grid grid-cols-3 gap-0.5 rounded-md p-0.5">
            {modes.map((item) => (
              <button
                key={item.mode}
                type="button"
                role="radio"
                aria-checked={mode === item.mode}
                onClick={() => setMode(item.mode)}
                className={mode === item.mode
                  ? 'bg-background text-foreground rounded px-2 py-1 text-xs font-medium shadow-xs'
                  : 'text-muted-foreground hover:text-foreground rounded px-2 py-1 text-xs'}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        {current && currentMode === null && (
          <p className="text-muted-foreground text-[11px]">Current filter: {summary}</p>
        )}
        {mode === 'within' && (
          <>
            <div className="grid grid-cols-2 gap-1.5" aria-label="Common time windows">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.hours}
                  type="button"
                  size="sm"
                  variant={relativeHours === preset.hours ? 'secondary' : 'outline'}
                  onClick={() => {
                    setDraftHours(preset.hours)
                    within(preset.hours)
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium">Custom window</span>
              <span className="text-right tabular-nums">
                <span className="block">{formatRecentWindow(draftHours)}</span>
                <span className="text-muted-foreground block text-[11px]">{formatRecentCutoff(draftHours)}</span>
              </span>
            </div>
            {slider(`${column.label} window`, draftHours, setDraftHours, within)}
          </>
        )}
        {mode === 'older' && (
          <>
            <div className="grid grid-cols-2 gap-1.5" aria-label="Common ages">
              {OLDER_PRESETS.map((preset) => (
                <Button
                  key={preset.hours}
                  type="button"
                  size="sm"
                  variant={currentMode === 'older' && age?.min && toHours(age.min) === preset.hours ? 'secondary' : 'outline'}
                  onClick={() => {
                    setDraftMin(preset.hours)
                    older(preset.hours)
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium">Older than</span>
              <span className="tabular-nums">{formatPeriod(draftMin)} ago</span>
            </div>
            {slider(`${column.label} minimum age`, draftMin, setDraftMin, older, (hours) => `Over ${formatPeriod(hours)} ago`)}
          </>
        )}
        {mode === 'between' && (
          <>
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium">From</span>
              <span className="tabular-nums">{formatPeriod(draftMin)} ago</span>
            </div>
            {slider(`${column.label} newest age`, draftMin, setDraftMin, (hours) => between(hours, draftMax), (hours) => `${formatPeriod(hours)} ago`)}
            <div className="flex items-start justify-between gap-3">
              <span className="font-medium">To</span>
              <span className="tabular-nums">{formatPeriod(draftMax)} ago</span>
            </div>
            {slider(`${column.label} oldest age`, draftMax, setDraftMax, (hours) => between(draftMin, hours), (hours) => `${formatPeriod(hours)} ago`)}
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={!current}
          onClick={() => {
            setDraftHours(DEFAULT_HOURS)
            apply(null)
          }}
        >
          <RotateCcw aria-hidden className="size-3.5" />
          Clear filter
        </Button>
      </DataGridFilterBody>
    </DataGridFilterPanel>
  )

  if (presentation !== 'chip') return panel

  return (
    <DataGridFilterChip column={column} summary={summary} open={open} onOpenChange={changeOpen}>
      {panel}
    </DataGridFilterChip>
  )
}
